import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePosContext } from "../context/pos-context";
import { PosItemRow } from "./pos-item-row";

export function PosVariantSelectorDialog() {
  const {
    selectedProduct,
    setSelectedProduct,
    cart,
    isDesktop,
  } = usePosContext();

  const open = selectedProduct !== null && (selectedProduct.productVariants?.length ?? 0) > 1;

  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setSelectedProduct(null);
    },
    [setSelectedProduct]
  );

  const initialQuantities = useMemo(() => {
    if (!selectedProduct?.productVariants) return new Map<string, number>();
    const map = new Map<string, number>();
    for (const v of selectedProduct.productVariants) {
      const id = String(v.id);
      map.set(id, cart.getQuantity(id));
    }
    return map;
  }, [selectedProduct?.productVariants, cart]);

  const [quantities, setQuantities] = useState<Map<string, number>>(initialQuantities);

  useEffect(() => {
    if (open) {
      setQuantities(initialQuantities);
    }
  }, [open, initialQuantities]);

  const handleQuantityChange = useCallback((variantId: string, delta: number) => {
    setQuantities((prev) => {
      const next = new Map(prev);
      const current = next.get(variantId) ?? 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        next.delete(variantId);
      } else {
        next.set(variantId, newQty);
      }
      return next;
    });
  }, []);

  const handleAddToCart = useCallback(() => {
    if (!selectedProduct) return;

    const { product, productVariants } = selectedProduct;
    const entries: Array<{
      item: { productId: string; variantId: string; name: string; imageUrl: string | null; price: number };
      quantity: number;
    }> = [];

    for (const variant of productVariants ?? []) {
      const variantId = String(variant.id);
      const qty = quantities.get(variantId) ?? 0;

      const price = typeof variant.price === "number" ? variant.price : 0;
      const name = variant.name ?? product.name;
      const imageUrl = variant.imageUrl ?? product.imageUrl ?? null;
      entries.push({
        item: { productId: product.id, variantId, name, imageUrl, price },
        quantity: qty,
      });
    }

    cart.syncItemsBatch(entries);

    onOpenChange(false);
  }, [selectedProduct, quantities, cart, onOpenChange]);

  const totalSelected = useMemo(
    () =>
      Array.from(quantities.values()).reduce((sum, q) => sum + q, 0),
    [quantities]
  );

  const subtotal = useMemo(() => {
    if (!selectedProduct?.productVariants) return 0;
    return selectedProduct.productVariants.reduce((sum, v) => {
      const variantId = String(v.id);
      const qty = quantities.get(variantId) ?? 0;
      const price = typeof v.price === "number" ? v.price : 0;
      return sum + price * qty;
    }, 0);
  }, [selectedProduct?.productVariants, quantities]);

  if (!selectedProduct || !open) return null;

  const { product, productVariants } = selectedProduct;
  const variants = productVariants ?? [];

  const content = (
    <div className="flex flex-col overflow-hidden max-h-[inherit]">
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-3 md:py-4 space-y-2 md:space-y-3">
        {variants.map((variant) => {
          const variantId = String(variant.id);
          const quantity = quantities.get(variantId) ?? 0;
          const price = variant.price ?? 0;
          const name = variant.name ?? product.name;
          return (
            <PosItemRow
              key={variant.id}
              name={name}
              imageUrl={variant.imageUrl ?? null}
              price={price}
              quantity={quantity}
              onIncrease={() => handleQuantityChange(variantId, 1)}
              onDecrease={() => handleQuantityChange(variantId, -1)}
              decreaseDisabled={quantity <= 0}
            />
          );
        })}
      </div>

      <div className="flex-shrink-0 border-t bg-white px-4 md:px-6 py-3 md:py-4 space-y-2 md:space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs md:text-sm text-muted-foreground">
            Total ({totalSelected} items)
          </span>
          <span className="text-xl md:text-2xl font-bold text-primary">
            ${subtotal.toFixed(2)}
          </span>
        </div>
        <Button
          onClick={handleAddToCart}
          disabled={totalSelected <= 0}
          className="w-full h-10 md:h-12 text-sm md:text-base font-semibold"
        >
          Add to Cart
        </Button>
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-4 md:px-6 pt-4 md:pt-6 pb-4 border-b">
            <DialogTitle>{product.name}</DialogTitle>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader className="text-left px-4 md:px-6 border-b">
          <DrawerTitle>{product.name}</DrawerTitle>
        </DrawerHeader>
        {content}
      </DrawerContent>
    </Drawer>
  );
}
